import { useEffect } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useAuth } from '@frontend/contexts/AuthContext'
import { Button } from '@frontend/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@frontend/components/ui/card'

const ERROR_MESSAGES: Record<string, string> = {
  invalid_domain: 'Only @edendata.com email addresses are allowed.',
  exchange_failed: 'Authentication failed. Please try again.',
  no_code: 'Authentication was cancelled.',
  oauth_init_failed: 'Failed to start authentication. Please try again.',
  missing_verifier: 'Session expired. Please try logging in again.',
}

export function Login() {
  const { user, loading, login } = useAuth()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  const error = searchParams.get('error')
  const errorMessage = error ? ERROR_MESSAGES[error] || 'An error occurred during login.' : null

  // Redirect to reviews if already logged in
  useEffect(() => {
    if (!loading && user) {
      navigate('/reviews', { replace: true })
    }
  }, [user, loading, navigate])

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background p-4">
      {/* Background decoration */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 right-0 h-[500px] w-[500px] rounded-full bg-primary/5 blur-3xl" />
        <div className="absolute -bottom-40 left-0 h-[500px] w-[500px] rounded-full bg-primary/5 blur-3xl" />
      </div>

      {/* Logo */}
      <div className="relative mb-8 flex items-center gap-3">
        <img src="/assets/logo.png" alt="EdenData" className="h-12 w-12 rounded-xl" />
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Garden</h1>
          <p className="text-sm text-muted-foreground">by Riveron</p>
        </div>
      </div>

      {/* Login card */}
      <Card className="relative w-full max-w-sm shadow-xl shadow-black/5">
        <CardHeader className="text-center">
          <CardTitle className="text-xl">Welcome back</CardTitle>
          <CardDescription>Sign in to access the admin dashboard</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {errorMessage && (
            <div className="rounded-lg border border-destructive/20 bg-destructive/10 p-3 text-sm text-destructive">
              {errorMessage}
            </div>
          )}
          <Button
            onClick={login}
            className="w-full bg-primary font-medium text-primary-foreground shadow-md shadow-primary/25 hover:bg-primary/90"
            size="lg"
          >
            <svg className="mr-2 h-5 w-5" viewBox="0 0 24 24">
              <path
                fill="currentColor"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="currentColor"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="currentColor"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="currentColor"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            Continue with Google
          </Button>
          <p className="text-center text-xs text-muted-foreground">
            Access restricted to <span className="font-medium">@edendata.com</span> accounts
          </p>
        </CardContent>
      </Card>
    </div>
  )
}
